/**
 * Gender Toggle Component
 * 
 * Manages male/female image switching in product gallery.
 * Swaps image src/srcset between female (default) and male versions.
 * Persists selection in localStorage.
 * Male images are matched by alt text containing '*MALE*'.
 */

import { Component } from './component.js';

class GenderToggleComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  init() {
    this.gallery = this.closest('media-gallery');
    this.buttons = this.querySelectorAll('.gender-toggle__button');
    
    // Get all toggleable images (both main gallery and zoom dialog have data-male-src)
    this.toggleableImages = this.gallery ? Array.from(this.gallery.querySelectorAll('img[data-male-src]')) : [];
    
    // Get all media containers - handle both grid layout (li) and slideshow layout (slideshow-slide)
    // Grid layout uses <li> elements with data-gender-toggle attribute
    // Slideshow layout uses <slideshow-slide> elements
    const gridItems = this.gallery ? Array.from(this.gallery.querySelectorAll('li[data-gender-toggle]')) : [];
    const slideshowSlides = this.gallery ? Array.from(this.gallery.querySelectorAll('slideshow-slide:not(.dialog-zoomed-gallery slideshow-slide)')) : [];
    this.mediaContainers = [...gridItems, ...slideshowSlides];
    
    // Get all thumbnail buttons
    this.thumbnailButtons = this.gallery ? Array.from(this.gallery.querySelectorAll('.slideshow-controls__thumbnail')) : [];
    
    if (!this.buttons.length) {
      return;
    }

    const isVariantPicker = !this.gallery;
    const isGalleryWithImages = this.gallery && this.toggleableImages.length > 0;

    if (!isVariantPicker && !isGalleryWithImages) {
      return;
    }

    // Variant picker: default to male. Gallery: use localStorage or female
    if (isVariantPicker) {
      this.activeGender = 'male';
    } else {
      this.activeGender = localStorage.getItem('activeGender') || 'female';
    }

    this.updateButtonStates();
    this.updateModelDetails(this.activeGender);
    
    // If male is stored preference, swap to male on load
    if (this.activeGender === 'male') {
      this.swapToGender('male');
    }

    if (this.gallery) {
      this.gallery.classList.remove('is-loading');
    }

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.handleToggleClick.bind(this));
    });

    this.syncWithOtherInstances();
  }

  /**
   * Syncs active gender state across all other gender-toggle-component instances on the page.
   * Called on init and on toggle click to keep gallery and variant picker in sync.
   */
  syncWithOtherInstances() {
    const others = document.querySelectorAll('gender-toggle-component');
    others.forEach((node) => {
      const other = /** @type {GenderToggleComponent} */ (node);
      if (other === this) return;
      other.activeGender = this.activeGender;
      other.updateButtonStates();
      if (other.gallery && other.toggleableImages && other.toggleableImages.length > 0) {
        other.swapToGender(this.activeGender);
      }
    });
  }

  handleToggleClick(e) {
    const button = e.currentTarget;
    const gender = button.dataset.gender;

    if (gender === this.activeGender) return;

    this.activeGender = gender;
    if (this.gallery) {
      localStorage.setItem('activeGender', gender);
    }

    this.updateButtonStates();
    if (this.gallery && this.toggleableImages && this.toggleableImages.length > 0) {
      this.swapToGender(gender);
    }

    this.syncWithOtherInstances();
  }

  updateButtonStates() {
    this.buttons.forEach((button) => {
      const isActive = button.dataset.gender === this.activeGender;
      button.setAttribute('aria-pressed', isActive);
      
      if (isActive) {
        button.classList.add('gender-toggle__button--active');
      } else {
        button.classList.remove('gender-toggle__button--active');
      }
    });
  }

  swapToGender(gender) {
    // Swap main gallery images first (only for images that have male counterparts)
    this.toggleableImages.forEach((img) => {
      // Only process images in main gallery slides, not zoom dialog
      if (img.closest('.dialog-zoomed-gallery')) return;
      
      if (gender === 'male') {
        // Swap to male: store female, load male
        if (!img.dataset.femaleSrc) {
          // First time swapping - store female values
          img.dataset.femaleSrc = img.getAttribute('src');
          img.dataset.femaleSrcset = img.getAttribute('srcset');
          img.dataset.femaleAlt = img.getAttribute('alt');
        }
        
        // Load male values
        img.setAttribute('src', img.dataset.maleSrc);
        img.setAttribute('srcset', img.dataset.maleSrcset);
        img.setAttribute('alt', img.dataset.maleAlt);
        
      } else {
        // Swap to female: restore female values
        if (img.dataset.femaleSrc) {
          img.setAttribute('src', img.dataset.femaleSrc);
          img.setAttribute('srcset', img.dataset.femaleSrcset);
          img.setAttribute('alt', img.dataset.femaleAlt);
        }
      }
    });
    
    // Then hide/show media containers based on gender selection
    this.mediaContainers.forEach((container) => {
      // Check if this is a male-only image
      const isMaleOnly = container.hasAttribute('data-male-only') || container.dataset.maleOnly === 'true';
      
      // Check the image's current alt text to see if it contains *MALE* (after swap)
      const img = container.querySelector('img');
      const currentAlt = img ? img.getAttribute('alt') : '';
      const isCurrentlyMale = currentAlt ? currentAlt.includes('*MALE*') : false;
      
      if (gender === 'male') {
        // Show images that are male-only OR have been swapped to male
        if (isMaleOnly || isCurrentlyMale) {
          container.removeAttribute('hidden');
          container.removeAttribute('aria-hidden');
          container.style.display = '';
        } else {
          // Hide containers that are not male (female images without male counterparts)
          container.setAttribute('hidden', '');
          container.setAttribute('aria-hidden', 'true');
          container.style.display = 'none';
        }
      } else {
        // When female is selected, hide male-only images, show others
        if (isMaleOnly) {
          container.setAttribute('hidden', '');
          container.setAttribute('aria-hidden', 'true');
          container.style.display = 'none';
        } else {
          container.removeAttribute('hidden');
          container.removeAttribute('aria-hidden');
          container.style.display = '';
        }
      }
    });
    
    // Also handle zoom dialog images
    const zoomDialogImages = this.toggleableImages.filter((img) => {
      return img.closest('.dialog-zoomed-gallery');
    });
    
    zoomDialogImages.forEach((img) => {
      if (gender === 'male') {
        if (!img.dataset.femaleSrc) {
          img.dataset.femaleSrc = img.getAttribute('src');
          img.dataset.femaleSrcset = img.getAttribute('srcset');
          img.dataset.femaleAlt = img.getAttribute('alt');
        }
        img.setAttribute('src', img.dataset.maleSrc);
        img.setAttribute('srcset', img.dataset.maleSrcset);
        img.setAttribute('alt', img.dataset.maleAlt);
      } else {
        if (img.dataset.femaleSrc) {
          img.setAttribute('src', img.dataset.femaleSrc);
          img.setAttribute('srcset', img.dataset.femaleSrcset);
          img.setAttribute('alt', img.dataset.femaleAlt);
        }
      }
    });
    
    // Update thumbnail buttons
    this.thumbnailButtons.forEach((button, index) => {
      const correspondingContainer = this.mediaContainers[index];
      if (!correspondingContainer) return;
      
      // Check if this is a male-only image
      const isMaleOnly = correspondingContainer.hasAttribute('data-male-only') || correspondingContainer.dataset.maleOnly === 'true';
      
      // Check the image's current alt text to see if it contains *MALE* (after swap)
      const containerImg = correspondingContainer.querySelector('img');
      const currentAlt = containerImg ? containerImg.getAttribute('alt') : '';
      const isCurrentlyMale = currentAlt ? currentAlt.includes('*MALE*') : false;
      
      if (gender === 'male') {
        // Show thumbnails for male-only images OR images that are currently displaying as male
        if (isMaleOnly || isCurrentlyMale) {
          button.style.display = '';
          button.removeAttribute('hidden');
        } else {
          // Hide thumbnail buttons for images that are not male
          button.style.display = 'none';
          button.setAttribute('hidden', '');
          return;
        }
      } else {
        // When female is selected, hide thumbnails for male-only images
        if (isMaleOnly) {
          button.style.display = 'none';
          button.setAttribute('hidden', '');
          return;
        } else {
          button.style.display = '';
          button.removeAttribute('hidden');
        }
      }

      const img = button.querySelector('img');
      if (!img) return;

      // Find the image in the corresponding container
      const containerImage = correspondingContainer.querySelector('img');
      if (!containerImage) return;
      const isMaleImage = Boolean(containerImage.dataset.maleSrc);

      if (gender === 'male') {
        // For male images, use current src (it's already the male image after swap)
        // For images with male counterparts, use male image data attributes
        if (isCurrentlyMale || isMaleImage) {
          img.src = containerImage.getAttribute('src') || '';
          img.srcset = containerImage.getAttribute('srcset') || '';
        } else {
          const maleSrc = containerImage.dataset.maleSrc;
          const maleSrcset = containerImage.dataset.maleSrcset;
          if (maleSrc) {
            img.src = maleSrc;
            if (maleSrcset) img.srcset = maleSrcset;
          }
        }
      } else {
        // Use female image - either from stored dataset or current src
        const femaleSrc = containerImage.dataset.femaleSrc || containerImage.getAttribute('src');
        const femaleSrcset = containerImage.dataset.femaleSrcset || containerImage.getAttribute('srcset');
        if (femaleSrc) {
          img.src = femaleSrc;
          if (femaleSrcset) img.srcset = femaleSrcset;
        }
      }
    });

    this.syncSlideshowPaginationToVisibleSlides();

    this.updateModelDetails(gender);
  }

  /**
   * Dots/counter are rendered for the female-default visible slide count (male-only slides
   * excluded in Liquid). In male mode extra female slides lose a counterpart after pairing
   * dedupe and are hidden on the slideshow-slide; slideshow.js ignores [hidden] slides, but
   * pagination controls stay at the larger count unless we prune them here.
   */
  syncSlideshowPaginationToVisibleSlides() {
    if (!this.gallery) return;

    const slideEls = Array.from(
      this.gallery.querySelectorAll('slideshow-slide:not(.dialog-zoomed-gallery slideshow-slide)'),
    );

    if (!slideEls.length) return;

    const visibleCount = slideEls.filter((slide) => !slide.hasAttribute('hidden')).length;

    const dotLists = this.gallery.querySelectorAll('ol.slideshow-controls__dots');
    dotLists.forEach((ol) => {
      ol.querySelectorAll(':scope > li').forEach((li, index) => {
        if (index >= visibleCount) {
          li.setAttribute('hidden', '');
          li.style.display = 'none';
          return;
        }

        li.removeAttribute('hidden');
        li.style.display = '';
      });
    });

    const counters = this.gallery.querySelectorAll('.slideshow-controls__counter');
    counters.forEach((counter) => {
      const slash = counter.querySelector('.slash');
      if (!slash?.nextSibling) return;

      if (slash.nextSibling.nodeType === Node.TEXT_NODE) {
        slash.nextSibling.textContent = String(visibleCount);
      }
    });

    const slideshow = this.gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const slides = slideshow.slides;
    if (!slides?.length) return;

    if (slideshow.current >= slides.length) {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /**
   * Sync model details copy with the active gender toggle.
   * Falls back to default copy when gender-specific copy is missing.
   * @param {string} gender
   */
  updateModelDetails(gender) {
    const modelDetailsElements = document.querySelectorAll('[data-model-details-gender="true"]');
    if (!modelDetailsElements.length) return;

    modelDetailsElements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const defaultDetails = element.dataset.modelDetailsDefault || '';
      const femaleDetails = element.dataset.modelDetailsFemale || '';
      const maleDetails = element.dataset.modelDetailsMale || '';

      let nextDetails = defaultDetails;

      if (gender === 'male' && maleDetails) {
        nextDetails = maleDetails;
      } else if (gender === 'female' && femaleDetails) {
        nextDetails = femaleDetails;
      } else {
        const derivedDetails = this.getDerivedModelDetails(defaultDetails, gender);
        if (derivedDetails) {
          nextDetails = derivedDetails;
        }
      }

      element.textContent = nextDetails;
    });
  }

  /**
   * Fallback parser for legacy combined model details copy:
   * "Female sentence. Male sentence"
   * @param {string} detailsText
   * @param {string} gender
   * @returns {string}
   */
  getDerivedModelDetails(detailsText, gender) {
    if (!detailsText) return '';

    const sentences = detailsText
      .split(/(?<=[.!?])\s+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    if (sentences.length < 2) return '';

    if (gender === 'male') {
      return sentences.slice(1).join(' ');
    }

    if (gender === 'female') {
      return sentences[0];
    }

    return '';
  }
}

customElements.define('gender-toggle-component', GenderToggleComponent);
